import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { URL_CONFIG } from "../../config/urlConfig";

export const SCREEN_DAMAGE_TEST_DATA = {
  TITLE: "Screen Damage Test",
  INSTRUCTION: {
    HINDI:
      "कृपया नीचे दिए गए बटन को दबाकर फुल स्क्रीन मोड में जाएं और स्क्रीन डैमेज टेस्ट शुरू करें।",
    ENGLISH:
      "Please tap the button below to enter full screen mode and begin the screen damage test.",
  },
  FULL_SCREEN_BUTTON_TEXT: "Zoom to Full Screen",
};

export const useScreenDamageTestData = () => {
  const navigate = useNavigate();
  const screenRef = useRef(null);
  const canvasRef = useRef(null);
  const bbpsid = localStorage.getItem("bbpsid");

  const [paths, setPaths] = useState([]);
  const [isTouching, setIsTouching] = useState(false);
  const [device, setDevice] = useState("unknown");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [coverage, setCoverage] = useState(0);

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(ua)) {
      setDevice("android");
    } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
      setDevice("ios");
    } else {
      setDevice("other");
    }
  }, []);

  const showScreenTestScreen = useMemo(() => {
    if (device === "ios") {
      return true;
    }
    if (device !== "ios" && isFullScreen) {
      return true;
    }
    return false;
  }, [device, isFullScreen]);

  // Prevent navigation during screen damage test
  useEffect(() => {
    if (!showScreenTestScreen) return;

    // Push a new state to prevent back navigation
    const currentUrl = window.location.href;
    window.history.pushState(null, null, currentUrl);

    // Handle browser back button
    const handlePopState = (event) => {
      // Push the state again to prevent going back
      window.history.pushState(null, null, currentUrl);
    };

    // Handle page unload/refresh
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave? The screen damage test will be lost.";
      return "Are you sure you want to leave? The screen damage test will be lost.";
    };

    // Add event listeners
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Only auto-close notification messages after 3s, not navigation prevention
    // So, do NOT remove these listeners after 3s; keep them for the duration of the test

    // Cleanup function
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [showScreenTestScreen]);

  useEffect(() => {
    if (!showScreenTestScreen) return;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "blue";
    ctx.lineWidth = URL_CONFIG.STROKE_WIDTH;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    paths.forEach((path) => {
      if (path.length > 1) {
        ctx.beginPath();
        ctx.moveTo(path[0].x, path[0].y);
        for (let i = 1; i < path.length; i++) {
          ctx.lineTo(path[i].x, path[i].y);
        }
        ctx.stroke();
      }
    });
    // Calculate coverage
    const imageData = ctx.getImageData(0, 0, width, height);
    let colored = 0;
    for (let i = 0; i < imageData.data.length; i += 4) {
      // If not white
      if (
        !(
          imageData.data[i] === 255 &&
          imageData.data[i + 1] === 255 &&
          imageData.data[i + 2] === 255
        )
      ) {
        colored++;
      }
    }
    const percent = ((colored / (width * height)) * 100).toFixed(2);
    setCoverage(percent);
  }, [paths, showScreenTestScreen]);

  const handleTouchStart = (e) => {
    const rect = screenRef.current.getBoundingClientRect();
    const touches = Array.from(e.touches).map((touch) => ({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }));
    setPaths((prev) => [...prev, touches]);
    setIsTouching(true);
  };

  const handleTouchMove = (e) => {
    if (!isTouching) return;
    const rect = screenRef.current.getBoundingClientRect();
    const touches = Array.from(e.touches).map((touch) => ({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }));
    setPaths((prev) => {
      const newPaths = [...prev];
      newPaths[newPaths.length - 1] = [
        ...newPaths[newPaths.length - 1],
        ...touches,
      ];
      return newPaths;
    });
  };

  const goFullscreen = () => {
    const elem = document.documentElement;
    setIsFullScreen(true);
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen(); // Safari
    }
  };

  const hadleOnTouchEnd = () => {
    setIsTouching(false);
    if (parseFloat(coverage) >= URL_CONFIG.SCREEN_COVERAGE_THRESHOLD) {
      console.log("✅ Success: Screen is fully covered!");
      navigate("/instructions");
    } else {
      console.log("❌ Error: Screen not fully covered!");
    }
  };

  return {
    screenRef,
    handleTouchStart,
    handleTouchMove,
    paths,
    goFullscreen,
    showScreenTestScreen,
    hadleOnTouchEnd,
    canvasRef,
    bbpsid,
  };
};
